import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import {
  LIST_QUICK_REPLIES,
  SEND_QUICK_REPLY,
  DELETE_QUICK_REPLY,
} from '~/services';
import {
  CreateQuickReplyDialog,
  AvailableTemplates,
} from '../QuickReplyDialog';
import { TemplateStatus } from '~/types';

interface TemplateVariable {
  name: string;
  position: number;
  example: string;
  component_type: string;
}

interface QuickReplyTemplate {
  _id: string;
  meta_template_name: string;
  status: TemplateStatus;
  template_id: string;
  template: {
    tbit_name: string;
    language: string;
    components: any[];
    variables?: TemplateVariable[];
  };
}

interface Channel {
  _id: string;
  messaging_product: string;
  whatsapp_number: string;
  templates: QuickReplyTemplate[];
}

interface TemplateComponent {
  type: string;
  parameters: Array<{
    type: string;
    text: string;
  }>;
}

interface QuickReplyDialogProps {
  open: boolean;
  onClose: () => void;
  personId: string;
  personChannelId: string;
  agentId: string;
  channelType?: string; // Optional filter for channel type (whatsapp, etc)
}

const QuickReplyDialog: React.FC<QuickReplyDialogProps> = ({
  open,
  onClose,
  personId,
  personChannelId,
  agentId,
  channelType,
}) => {
  const [t] = useTranslation('activity');
  const navigate = useNavigate();

  const dialogRef = useRef<HTMLDialogElement>(null);
  const [selectedChannel, setSelectedChannel] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] =
    useState<QuickReplyTemplate | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<
    QuickReplyTemplate[]
  >([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [variableValues, setVariableValues] = useState<Record<string, string>>(
    {},
  );
  const [showConfirmDelete, setShowConfirmDelete] = useState<string | null>(
    null,
  );

  // Fetch quick replies
  const {
    data: quickRepliesData,
    loading: quickRepliesLoading,
    refetch: refetchQuickReplies,
  } = useQuery(LIST_QUICK_REPLIES, {
    variables: {
      data: {
        agent_id: agentId,
      },
    },
    fetchPolicy: 'network-only', // Don't use cache, always make a new request
    skip: !open, // Only fetch when dialog is open
  });

  // Filter channels by type if specified
  const channels = quickRepliesData?.response?.data || [];
  const filteredChannels = channelType
    ? channels.filter(
        (channel: Channel) => channel.messaging_product === channelType,
      )
    : channels;

  // Update available templates when selected channel changes
  useEffect(() => {
    if (selectedChannel) {
      const channel = channels.find((c: Channel) => c._id === selectedChannel);
      setAvailableTemplates(channel?.templates || []);
      // Reset selected template when channel changes
      setSelectedTemplate(null);
      setVariableValues({});
    } else {
      setAvailableTemplates([]);
      setSelectedTemplate(null);
      setVariableValues({});
    }
  }, [selectedChannel, channels]);

  // Update dialog visibility when open prop changes
  useEffect(() => {
    if (open) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [open]);

  // Auto-select channel when there's only one available
  useEffect(() => {
    if (
      filteredChannels.length === 1 &&
      !selectedChannel &&
      !quickRepliesLoading
    ) {
      setSelectedChannel(filteredChannels[0]._id);
    }
  }, [filteredChannels, selectedChannel, quickRepliesLoading]);

  // Initialize variable values when a template is selected
  useEffect(() => {
    if (selectedTemplate?.template?.variables?.length) {
      const initialValues: Record<string, string> = {};
      selectedTemplate.template.variables.forEach((variable) => {
        initialValues[variable.name] = variable.example || '';
      });
      setVariableValues(initialValues);
    } else {
      setVariableValues({});
    }
  }, [selectedTemplate]);

  // Send quick reply mutation
  const [sendQuickReply, { loading: sendingTemplate }] = useMutation(
    SEND_QUICK_REPLY,
    {
      onCompleted: (data) => {
        // Extract conversation ID from the response
        const conversationId = data?.response?.data?.conversation_id;
        if (conversationId) {
          // Navigate to the conversation
          navigate(
            `/agent/${agentId}/conversations/${personId}?conversationId=${conversationId}`,
          );
        }
        onClose();
      },
      onError: (error) => {
        console.error('Error sending quick reply:', error);
        // Keep dialog open on error
      },
    },
  );

  // Delete template mutation
  const [deleteQuickReply, { loading: deletingTemplate }] = useMutation(
    DELETE_QUICK_REPLY,
    {
      onCompleted: () => {
        refetchQuickReplies();
        setShowConfirmDelete(null);
      },
      onError: (error) => {
        console.error('Error deleting template:', error);
        setShowConfirmDelete(null);
      },
    },
  );

  const handleSendQuickReply = () => {
    if (!selectedChannel || !selectedTemplate) return;

    // Prepare components array for the template variables
    const components: TemplateComponent[] = [];

    if (selectedTemplate.template.variables?.length) {
      // Create a component for each variable in the correct order
      const orderedVariables = [...selectedTemplate.template.variables].sort(
        (a, b) => a.position - b.position,
      );

      // Format components for the API according to the mutation schema
      orderedVariables.forEach((variable) => {
        components.push({
          type: variable.component_type,
          parameters: [
            {
              type: 'text',
              text: variableValues[variable.name] || variable.example,
            },
          ],
        });
      });
    }

    sendQuickReply({
      variables: {
        data: {
          channel_id: selectedChannel,
          person_channel_id: personChannelId,
          template_name: selectedTemplate.meta_template_name,
          ...(components.length > 0 && { components }),
        },
      },
    });
  };

  const handleDeleteTemplate = (templateId: string) => {
    deleteQuickReply({
      variables: {
        data: {
          template_id: templateId,
          agent_id: agentId,
        },
      },
    });
  };

  const handleCreateSuccess = () => {
    // Close the create dialog and refresh the templates list
    setShowCreateDialog(false);
    refetchQuickReplies();
  };

  const handleTemplateSelect = (template: QuickReplyTemplate | null) => {
    setSelectedTemplate(template);
    if (template?.template?.variables?.length) {
      const initialValues: Record<string, string> = {};
      template.template.variables.forEach((variable) => {
        initialValues[variable.name] = variable.example || '';
      });
      setVariableValues(initialValues);
    } else {
      setVariableValues({});
    }
  };

  const handleRefreshTemplates = () => {
    refetchQuickReplies();
  };

  const renderVariableInputs = () => {
    if (!selectedTemplate?.template?.variables?.length) return null;

    return (
      <div className="mt-6 space-y-4">
        <h3 className="text-lg font-medium">{t('customize_template')}</h3>
        <p className="text-sm text-gray-500">
          {t('customize_template_description')}
        </p>
        <div className="space-y-3">
          {selectedTemplate.template.variables.map((variable) => (
            <div key={variable.name} className="form-control">
              <label className="label">
                <span className="label-text font-medium">{variable.name}</span>
                <span className="label-text-alt text-gray-500">
                  ({t('position')}: {variable.position})
                </span>
              </label>
              <input
                type="text"
                className="input-bordered input w-full"
                value={variableValues[variable.name] || ''}
                placeholder={variable.example}
                onChange={(e) =>
                  setVariableValues({
                    ...variableValues,
                    [variable.name]: e.target.value,
                  })
                }
              />
              {!variableValues[variable.name] && (
                <label className="label">
                  <span className="label-text-alt text-amber-500">
                    {t('default_value_will_be_used')}
                  </span>
                </label>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPreviewTemplate = () => {
    if (!selectedTemplate) return null;

    if (!selectedTemplate.template.components?.[0]?.text) return null;

    let previewText = selectedTemplate.template.components[0].text;

    // If this template has variables, highlight them in the preview
    if (selectedTemplate.template.variables?.length) {
      // Create a map of positions to variable names for easier lookup
      const positionToName = selectedTemplate.template.variables.reduce(
        (map, variable) => {
          map[variable.position] = variable.name;
          return map;
        },
        {} as Record<number, string>,
      );

      // Sort by position in descending order to avoid offset issues when replacing
      const sortedVars = [...selectedTemplate.template.variables].sort(
        (a, b) => b.position - a.position,
      );

      for (const variable of sortedVars) {
        const value = variableValues[variable.name] || variable.example;

        // Replace the {{number}} placeholder with the variable value or name
        previewText = previewText.replace(
          new RegExp(`\\{\\{${variable.position}\\}\\}`, 'g'),
          `<span class="text-primary font-medium bg-primary bg-opacity-10 px-1 rounded">${
            value || `{{${variable.name}}}`
          }</span>`,
        );
      }
    }

    return (
      <div className="mt-6 rounded-lg border bg-gray-50 p-4">
        <h3 className="mb-2 text-lg font-medium">{t('preview')}</h3>
        <div
          className="text-gray-800"
          dangerouslySetInnerHTML={{ __html: previewText }}
        />
      </div>
    );
  };

  const renderLoader = () => (
    <div className="flex items-center justify-center py-8">
      <div className="loading loading-spinner loading-lg"></div>
    </div>
  );

  const renderNoChannels = () => (
    <div className="py-6 text-center">
      <div className="mb-4 text-gray-600">{t('no_channels_connected')}</div>
      <p className="mb-4 text-sm text-gray-500">
        {t('connect_channel_to_use_quick_replies')}
      </p>
      <button
        className="btn btn-primary rounded-full"
        onClick={() => navigate(`/agent/${agentId}/settings/advanced`)}
      >
        {t('go_to_settings')}
      </button>
    </div>
  );

  const renderNoTemplates = () => (
    <div className="py-4 text-center">
      <div className="mb-3 text-gray-600">{t('no_quick_replies')}</div>
      <p className="mb-4 text-sm text-gray-500">
        {t('create_quick_reply_to_respond_faster')}
      </p>
      <button
        className="btn btn-primary rounded-full"
        onClick={() => {
          setShowCreateDialog(true);
        }}
      >
        {t('create_new_template')}
      </button>
    </div>
  );

  const renderDialogContent = () => {
    if (quickRepliesLoading) {
      return renderLoader();
    }

    const hasChannels = filteredChannels.length > 0;
    const hasTemplates = availableTemplates.length > 0;

    if (!hasChannels) {
      return renderNoChannels();
    }

    return (
      <>
        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            {t('select_channel')}
          </label>
          <select
            className="select-bordered select w-full"
            value={selectedChannel}
            onChange={(e) => setSelectedChannel(e.target.value)}
            disabled={sendingTemplate || deletingTemplate}
          >
            <option value="">{t('choose_channel')}</option>
            {filteredChannels.map((channel: Channel) => (
              <option key={channel._id} value={channel._id}>
                {channel.whatsapp_number} ({channel.messaging_product})
              </option>
            ))}
          </select>
        </div>

        {selectedChannel && !hasTemplates && renderNoTemplates()}
        {selectedChannel && hasTemplates && (
          <AvailableTemplates
            templates={availableTemplates}
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={handleTemplateSelect}
            loading={quickRepliesLoading}
            onRefresh={handleRefreshTemplates}
            onCreateNew={() => setShowCreateDialog(true)}
            onDelete={handleDeleteTemplate}
            t={t}
          />
        )}

        {selectedTemplate && renderVariableInputs()}
        {selectedTemplate && renderPreviewTemplate()}

        <div className="mt-6 flex justify-end space-x-2">
          <button
            className="btn btn-primary rounded-full"
            onClick={onClose}
            disabled={sendingTemplate || deletingTemplate}
          >
            {t('cancel')}
          </button>
          {selectedTemplate && (
            <button
              className="btn btn-primary rounded-full"
              onClick={handleSendQuickReply}
              disabled={
                !selectedChannel ||
                !selectedTemplate ||
                selectedTemplate.status !== 'APPROVED' ||
                sendingTemplate
              }
            >
              {sendingTemplate ? (
                <>
                  <span className="loading loading-spinner loading-xs"></span>
                  {t('sending')}
                </>
              ) : (
                t('send')
              )}
            </button>
          )}
        </div>

        {selectedTemplate && selectedTemplate.status !== 'APPROVED' && (
          <div className="text-error mt-2 text-sm">
            {t('cannot_send_template_not_approved')}
          </div>
        )}
      </>
    );
  };

  if (!open) return null;

  // Render CreateQuickReplyDialog as a separate component outside the main dialog
  if (showCreateDialog) {
    return (
      <>
        <dialog ref={dialogRef} className="modal-open modal">
          <div className="modal-box flex max-w-3xl flex-col gap-2">
            <h2 className="mb-4 text-xl font-semibold text-gray-800">
              {t('send_quick_reply')}
            </h2>
            {renderDialogContent()}
          </div>
          <form
            method="dialog"
            className="modal-backdrop"
            onSubmit={(e) => {
              e.preventDefault();
              // Don't close the dialog when clicking outside if the create dialog is open
            }}
          ></form>
        </dialog>
        <CreateQuickReplyDialog
          channel={channels.find((c: Channel) => c._id === selectedChannel)}
          open={showCreateDialog}
          onOpenChange={(isOpen: boolean) => setShowCreateDialog(isOpen)}
          onSuccess={handleCreateSuccess}
          agentId={agentId}
        />
      </>
    );
  }

  return (
    <dialog ref={dialogRef} onClose={onClose} className="modal">
      <div className="modal-box flex max-w-3xl flex-col gap-2">
        <h2 className="mb-4 text-xl font-semibold text-gray-800">
          {t('send_quick_reply')}
        </h2>
        {renderDialogContent()}
      </div>
      <form method="dialog" className="modal-backdrop">
        <button onClick={onClose}>close</button>
      </form>
    </dialog>
  );
};

export default QuickReplyDialog;
